import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

// ── Types ────────────────────────────────────────────────────────────────────

export interface CashbookEntry {
  id: string;
  transactionId: string;
  date: string;
  narration: string;
  voucherType: string;
  debit: number;
  credit: number;
  runningBalance: number;
  accountCode: string;
  accountName: string;
  createdBy: string;
  createdAt: string;
}

export interface CashbookResponse {
  entries: CashbookEntry[];
  balance: number;
  accounts: { code: string; name: string }[];
}

export interface CashbookCategories {
  expenseCategories: string[];
  incomeCategories: string[];
  cashAccounts: string[];
}

export interface CreateCashbookEntryPayload {
  type: 'in' | 'out';
  amount: number;
  category: string;
  cashAccount: string;
  date: string;
  narration?: string;
}

// ── Hooks ────────────────────────────────────────────────────────────────────

export const useTransactions = (filters?: { account?: string; startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ['cashbook', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.account) params.append('account', filters.account);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      const response = await api.get(`/api/v2/accounts/cashbook?${params.toString()}`);
      return response.data as CashbookResponse;
    },
  });
};

export const useCashbookCategories = () => {
  return useQuery({
    queryKey: ['cashbook', 'categories'],
    queryFn: async () => {
      const response = await api.get('/api/v2/accounts/cashbook/categories');
      return response.data as CashbookCategories;
    },
    staleTime: Infinity,
  });
};

export const useCreateTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateCashbookEntryPayload) => {
      const response = await api.post('/api/v2/accounts/cashbook/entry', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
    },
  });
};

// Legacy export kept for compatibility — no longer used by the new widget
export const useGenerateVoucher = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (_transactionId: string) => {
      // Vouchers are now auto-generated on entry creation — this is a no-op
      return { message: 'Voucher already generated on creation.' };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
    },
  });
};
